import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { Plus, Search, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Pagination } from '@/components/ui/pagination';
import { useClients, useCreateClient } from '@/hooks/queries';
import { LegalForm, AccountingType, VatRegime } from '@taxistry/types';

const LEGAL_FORM_OPTIONS: { value: LegalForm; label: string }[] = [
  { value: LegalForm.AE, label: 'Α.Ε.' },
  { value: LegalForm.EPE, label: 'Ε.Π.Ε.' },
  { value: LegalForm.IKE, label: 'Ι.Κ.Ε.' },
  { value: LegalForm.OE, label: 'Ο.Ε.' },
  { value: LegalForm.EE, label: 'Ε.Ε.' },
  { value: LegalForm.ATOMIKI, label: 'Ατομική' },
];

const VAT_REGIME_OPTIONS: { value: VatRegime; label: string }[] = [
  { value: VatRegime.MONTHLY, label: 'Μηνιαίο' },
  { value: VatRegime.QUARTERLY, label: 'Τριμηνιαίο' },
  { value: VatRegime.EXEMPT, label: 'Απαλλαγή' },
];

const ACCOUNTING_TYPE_OPTIONS: { value: AccountingType; label: string }[] = [
  { value: AccountingType.SINGLE_ENTRY, label: 'Απλογραφικά' },
  { value: AccountingType.DOUBLE_ENTRY, label: 'Διπλογραφικά' },
];

export function ClientsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [legalFormFilter, setLegalFormFilter] = useState<string>('');
  const [vatFilter, setVatFilter] = useState<string>('');
  const [tagFilter, setTagFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data, isLoading } = useClients({
    page,
    limit,
    search: search || undefined,
    legalForm: (legalFormFilter || undefined) as LegalForm | undefined,
    vatRegime: (vatFilter || undefined) as VatRegime | undefined,
    tag: tagFilter || undefined,
    sortBy: 'businessName',
    sortOrder: 'asc',
  });

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="font-display text-xl font-bold">{t('clients.title')}</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              {t('clients.add')}
            </Button>
          </DialogTrigger>
          <CreateClientDialog onClose={() => setDialogOpen(false)} />
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="shadow-none border-border">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('clients.search')}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-9 rounded-[10px] bg-surface-2"
              />
            </div>
            <Select value={legalFormFilter} onValueChange={(v) => { setLegalFormFilter(v === '__all__' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder={t('clients.legalForm')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Όλες</SelectItem>
                {LEGAL_FORM_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={vatFilter} onValueChange={(v) => { setVatFilter(v === '__all__' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder={t('clients.vatRegime')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Όλα</SelectItem>
                {VAT_REGIME_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Ετικέτα..."
                value={tagFilter}
                onChange={(e) => { setTagFilter(e.target.value); setPage(1); }}
                className="pl-9 w-[140px] rounded-[10px] bg-surface-2"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('clients.businessName')}</TableHead>
              <TableHead>{t('clients.afm')}</TableHead>
              <TableHead>{t('clients.legalForm')}</TableHead>
              <TableHead>{t('clients.vatRegime')}</TableHead>
              <TableHead>{t('clients.sector')}</TableHead>
              <TableHead>Ετικέτες</TableHead>
              <TableHead className="text-right">{t('clients.contacts')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 7 }).map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-5 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : !data?.data.length ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  {t('clients.noClients')}
                </TableCell>
              </TableRow>
            ) : (
              data.data.map((client) => (
                <TableRow
                  key={client.id}
                  className="cursor-pointer table-row-hover"
                  onClick={() => navigate({ to: '/clients/$clientId', params: { clientId: client.id } })}
                >
                  <TableCell className="font-semibold text-[13px]">{client.businessName}</TableCell>
                  <TableCell><span className="font-data text-sm">{client.afm}</span></TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {t(`clients.legalForms.${client.legalForm}`)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {t(`clients.vatRegimes.${client.vatRegime}`)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{client.sector ?? '—'}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(client.tags ?? []).map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs px-1.5 py-0">{tag}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{String((client as unknown as Record<string, unknown>).contactsCount ?? '—')}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {data && (
          <div className="px-4 py-3 border-t">
            <Pagination
              page={data.page}
              totalPages={data.totalPages}
              total={data.total}
              limit={limit}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Create Client Dialog ─────────────────────

function CreateClientDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const createClient = useCreateClient();
  const [form, setForm] = useState({
    businessName: '',
    afm: '',
    legalForm: LegalForm.IKE,
    accountingType: AccountingType.DOUBLE_ENTRY,
    vatRegime: VatRegime.QUARTERLY,
    sector: '',
    kadCode: '',
    hasEmployees: false,
    hasRentalIncome: false,
    hasProperty: false,
    notes: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createClient.mutateAsync({
      ...form,
      sector: form.sector || undefined,
      kadCode: form.kadCode || undefined,
      notes: form.notes || undefined,
    });
    onClose();
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{t('clients.add')}</DialogTitle>
        <DialogDescription>Συμπληρώστε τα στοιχεία του νέου πελάτη</DialogDescription>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label htmlFor="businessName">{t('clients.businessName')} *</Label>
            <Input
              id="businessName"
              value={form.businessName}
              onChange={(e) => setForm((f) => ({ ...f, businessName: e.target.value }))}
              required
            />
          </div>
          <div>
            <Label htmlFor="afm">{t('clients.afm')} *</Label>
            <Input
              id="afm"
              value={form.afm}
              onChange={(e) => setForm((f) => ({ ...f, afm: e.target.value.replace(/\D/g, '').slice(0, 9) }))}
              maxLength={9}
              pattern="\d{9}"
              required
            />
          </div>
          <div>
            <Label>{t('clients.legalForm')}</Label>
            <Select value={form.legalForm} onValueChange={(v) => setForm((f) => ({ ...f, legalForm: v as LegalForm }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {LEGAL_FORM_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Λογιστικό Σύστημα</Label>
            <Select value={form.accountingType} onValueChange={(v) => setForm((f) => ({ ...f, accountingType: v as AccountingType }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACCOUNTING_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('clients.vatRegime')}</Label>
            <Select value={form.vatRegime} onValueChange={(v) => setForm((f) => ({ ...f, vatRegime: v as VatRegime }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {VAT_REGIME_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('clients.sector')}</Label>
            <Input
              value={form.sector}
              onChange={(e) => setForm((f) => ({ ...f, sector: e.target.value }))}
              placeholder="π.χ. Λιανικό εμπόριο"
            />
          </div>
          <div>
            <Label>ΚΑΔ</Label>
            <Input
              value={form.kadCode}
              onChange={(e) => setForm((f) => ({ ...f, kadCode: e.target.value }))}
              placeholder="π.χ. 47.11"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.hasEmployees}
              onChange={(e) => setForm((f) => ({ ...f, hasEmployees: e.target.checked }))}
              className="rounded border-input"
            />
            Έχει εργαζόμενους
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.hasRentalIncome}
              onChange={(e) => setForm((f) => ({ ...f, hasRentalIncome: e.target.checked }))}
              className="rounded border-input"
            />
            Εισόδημα από ενοίκια
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.hasProperty}
              onChange={(e) => setForm((f) => ({ ...f, hasProperty: e.target.checked }))}
              className="rounded border-input"
            />
            Ακίνητη περιουσία
          </label>
        </div>

        <div>
          <Label>Σημειώσεις</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            rows={3}
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={createClient.isPending}>
            {createClient.isPending ? t('common.loading') : t('common.create')}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
